/**
 * `sync.studyGroups.group(id).members.*` — member roster reads + self-only
 * `markOpened` / `markRead` cursor updates.
 */

import type { SGMember } from '@flashka/shared/sync-engine/schema/group-entities';

import { useShallow } from 'zustand/react/shallow';

import { useStudyGroupStore } from '../internal/store/study-group-store';
import { currentUserId, nowISO, sendSG } from './helpers';
import type { SGScopedGroup } from './types';

export function createMembersApi(groupId: string): SGScopedGroup['members'] {
  return {
    use: () =>
      useStudyGroupStore(
        useShallow((s) => Object.values(s.groups[groupId]?.members ?? {})),
      ),
    useFind: (userId: string) =>
      useStudyGroupStore((s) => s.groups[groupId]?.members[userId]),
    markOpened: () => {
      const uid = currentUserId();
      if (!uid) return;
      const now = nowISO();
      const current = useStudyGroupStore.getState().groups[groupId]?.members[uid];
      if (!current) return;
      const next: SGMember = {
        ...current,
        lastOpenedAtISO: now,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().mergeMembers(groupId, [next]);
      sendSG(groupId, {
        type: 'sg-members-push',
        sgMembers: [next],
        updatedAtISO: now,
      });
    },
    markRead: () => {
      const uid = currentUserId();
      if (!uid) return;
      const now = nowISO();
      const current = useStudyGroupStore.getState().groups[groupId]?.members[uid];
      if (!current) return;
      const next: SGMember = {
        ...current,
        lastReadAtISO: now,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().mergeMembers(groupId, [next]);
      sendSG(groupId, {
        type: 'sg-members-push',
        sgMembers: [next],
        updatedAtISO: now,
      });
    },
    setLeaderboardOptOut: (value: boolean) => {
      // Server-synced opt-out from the group leaderboard ranking. Same
      // pattern as markOpened/markRead — optimistic local merge + WS push.
      // Server filters opted-out members from `ranking[]` for OTHER
      // viewers (the requester themselves still sees their row, with
      // `hiddenSelf=true`, in the REST response).
      const uid = currentUserId();
      if (!uid) return;
      const now = nowISO();
      const current = useStudyGroupStore.getState().groups[groupId]?.members[uid];
      if (!current) return;
      const next: SGMember = {
        ...current,
        leaderboardOptOut: value,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().mergeMembers(groupId, [next]);
      sendSG(groupId, {
        type: 'sg-members-push',
        sgMembers: [next],
        updatedAtISO: now,
      });
    },
  };
}
